/**
 * ExceptionPanel — warning-light panel showing count + top examples.
 * Each panel has an "Investigate" link to the relevant diagnostics page.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import type { ExceptionGroup } from '@/types/cockpit';

export interface ExceptionPanelProps {
  title: string;
  group: ExceptionGroup;
  /** URL for "Investigate" link */
  investigateHref: string;
  /** Optional extra info per example row */
  extraField?: 'type' | 'days' | 'lagDays';
  className?: string;
}

export const ExceptionPanel: React.FC<ExceptionPanelProps> = ({
  title,
  group,
  investigateHref,
  extraField,
  className,
}) => {
  const navigate = useNavigate();
  const hasIssues = group.count > 0;

  return (
    <div
      className={cn(
        'rounded-lg border bg-card p-3 flex flex-col gap-2 min-w-0',
        hasIssues ? 'border-l-4 border-l-amber-500' : '',
        className,
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-[11px] font-medium text-muted-foreground">{title}</h4>
        <span
          className={cn(
            'text-sm font-bold',
            hasIssues ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-500',
          )}
        >
          {group.count}
        </span>
      </div>

      {/* Top examples */}
      {hasIssues && group.topExamples.length > 0 && (
        <ul className="space-y-0.5">
          {group.topExamples.map((ex) => (
            <li key={ex.id} className="text-[11px] text-muted-foreground flex items-baseline gap-1 leading-tight">
              <span className="text-amber-500 shrink-0">•</span>
              <span className="truncate" title={ex.name}>
                {ex.name}
              </span>
              {extraField === 'type' && ex.type && (
                <span className="text-[10px] text-muted-foreground/60 shrink-0 ml-auto">{ex.type}</span>
              )}
              {extraField === 'days' && ex.days !== undefined && (
                <span className="text-[10px] text-muted-foreground/60 shrink-0 ml-auto">{ex.days}d</span>
              )}
              {extraField === 'lagDays' && ex.lagDays !== undefined && (
                <span className="text-[10px] text-muted-foreground/60 shrink-0 ml-auto">{ex.lagDays}d lag</span>
              )}
            </li>
          ))}
        </ul>
      )}

      {!hasIssues && (
        <p className="text-[11px] text-emerald-500">No issues detected</p>
      )}

      {/* Investigate link */}
      {hasIssues && (
        <button
          type="button"
          onClick={() => navigate(investigateHref)}
          className="text-[11px] text-primary hover:underline text-left mt-auto"
        >
          Investigate →
        </button>
      )}
    </div>
  );
};
